import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const TABS = [
  { label: "Home", path: "/" },
  { label: "Team", path: "/about" },
  { label: "Capabilities", path: "/capabilities" },
  { label: "Certifications", path: "/certifications" },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group text-white">
          <Logo className="text-[26px]" wordmark={false} />
          <span className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.25em] text-white/50 group-hover:text-blue-400 transition-colors">
            Sabala Management
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {TABS.map((t) => {
            const active = pathname === t.path;
            return (
              <Link
                key={t.path}
                to={t.path}
                data-testid={`nav-link-${t.label.toLowerCase()}`}
                className="relative px-5 py-2 text-sm font-semibold tracking-wide uppercase"
              >
                <span className={active ? "text-white" : "text-white/55 hover:text-white transition-colors"}>
                  {t.label}
                </span>
                {active && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute left-4 right-4 -bottom-[2px] h-[2px] bg-[#0055FF]"
                  />
                )}
              </Link>
            );
          })}
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="ml-4 px-5 py-2.5 bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-bold uppercase tracking-wide transition-transform hover:scale-[1.04]"
          >
            Start a Project
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {TABS.map((t) => (
                <Link
                  key={t.path}
                  to={t.path}
                  data-testid={`mobile-nav-link-${t.label.toLowerCase()}`}
                  className={`py-3 text-lg font-heading font-bold uppercase tracking-tight ${
                    pathname === t.path ? "text-blue-400" : "text-white/70"
                  }`}
                >
                  {t.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="mt-3 px-5 py-3 bg-[#0055FF] text-white text-center font-bold uppercase tracking-wide text-sm"
              >
                Start a Project
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
